import { PlusOutlined } from '@ant-design/icons';
import { Button, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { InfoTableHelper, WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { UserData, useAuth } from '@hooks';
import { getUsers } from '@hooks/useUsers/useUsers';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { DeleteUserModal } from './DeleteUserModal';
import { UserFormModal } from './UserFormModal';

const { Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

export const UsersPage = () => {
  const { profile: { data: currentUser } } = useAuth();

  // States to control modals
  const [userFormModalOpen, setUserFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  // States for pagination and search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  // Parameters for user search
  const params = {
    page,
    limit,
    search,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch users using the hook
  const { data, isLoading, refetch } = getUsers(params);

  // Refetch when params change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, refetch]);

  // Check if data exists before accessing properties
  const users = data?.data || [];
  const total = data?.pagination?.totalCount || 0;

  // Open modal to add a new user
  const openAddUserModal = () => {
    setSelectedUser(null);
    setUserFormModalOpen(true);
  };

  // Open modal to edit an existing user
  const openEditUserModal = (user: UserData) => {
    setSelectedUser(user);
    setUserFormModalOpen(true);
  };

  // Open modal to confirm user deletion
  const openDeleteModal = (user: UserData) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize || 10);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current || 1);
      return;
    }

    // Verifique se sorter é um array e use o primeiro elemento se for
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (activeSorter.field !== sortField || activeSorter.order !== sortOrder) {
      setSortField(activeSorter.field as string);
      setSortOrder(activeSorter.order as string);
      setPage(1);
    }
  };

  const columns: TableColumnsType<UserData> = [
    {
      title: 'USER',
      key: 'user',
      dataIndex: 'physicalPerson',
      render: (_: any, record: UserData) => (
        <InfoTableHelper
          title={record.physicalPerson?.name || record.email}
          className="font-orator text-lg"
          subTitle={record.email}
        />
      ),
      sorter: true,
    },
    {
      title: 'PROFILE',
      key: 'profile',
      dataIndex: 'profile',
      render: (_: any, record: UserData) => (
        <span className="font-orator uppercase tracking-wider text-md">{record.profile || 'user'}</span>
      ),
      sorter: true,
    },
    {
      title: 'TIER',
      key: 'tier',
      dataIndex: 'tier',
      render: (_: any, record: UserData) => (
        <span className="font-orator uppercase tracking-wider text-md">{record.tier || 'retail'}</span>
      ),
      sorter: true,
    },
    {
      title: 'CREATED AT',
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (_: any, record: UserData) =>
        <span className="font-orator uppercase text-md">{record.createdAt ? dayjs(record.createdAt).format('MM/DD/YYYY') : '-'}</span>,
      sorter: true,
    },
    {
      title: 'LAST UPDATED',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      render: (_: any, record: UserData) =>
        <span className="font-orator uppercase text-md">{record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YYYY - HH:mm') : '-'}</span>,
      sorter: true,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      render: (_: any, record: UserData) => (
        <div className="flex gap-2">
          <Tooltip title="Edit">
            <Button
              icon={<PencilSimple size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                openEditUserModal(record);
              }}
              type="text"
              className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#3B82F6] !bg-transparent hover:!bg-transparent"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<Trash size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(record);
              }}
              type="text"
              danger
              disabled={record.id === currentUser?.id}
              className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#F74902] !bg-transparent hover:!bg-transparent"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[500px]">
      <div className="bg-[url(/assets/images/AboutBackground.png)] bg-center bg-cover absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <div className="font-displayed !font-normal text-orangicide text-6xl text-center">
            USERS
          </div>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-orator !text-white text-center md:!text-lg lg:!text-3xl pt-4">
              Manage all users in the system
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <div className="font-displayed !font-normal md:!text-lg lg:!text-5xl text-orangicide">USERS LIST</div>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={openAddUserModal}
              className="btn-orangicide text-black font-orator !text-xl w-45 !h-12"
            >
              ADD USER
            </Button>
          </div>

          <Paragraph className="!text-white font-orator !text-xl tracking-wider">
            View, edit or remove users from the system
          </Paragraph>

          <WrapperPage isLoading={isLoading}>
            <GenericList<UserData>
              columns={columns}
              dataSource={users}
              onRow={(record) => ({
                onClick: () => openEditUserModal(record),
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for adding/editing user */}
          <UserFormModal
            open={userFormModalOpen}
            onCancel={() => setUserFormModalOpen(false)}
            editingUser={selectedUser}
          />

          {/* Modal to confirm deletion */}
          <DeleteUserModal
            open={deleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            user={selectedUser}
            onSuccess={refetch}
          />
        </div>
      </section>
    </div>
  );
}
